/* eslint-disable @typescript-eslint/triple-slash-reference */
/// <reference path="../../../../shared/src/types/facebook-instant.d.ts" />
/// <reference path="../../../../shared/src/types/reddit-devvit.d.ts" />
/* eslint-enable @typescript-eslint/triple-slash-reference */
import type {
  InstantGamesSDK,
  InstantGamesPlatform,
  ShareOptions,
} from '@crackpoint/shared/types';
import { PRODUCT_FULL_NAME } from './constants';

// Detect the current platform
export function detectPlatform(): InstantGamesPlatform {
  if (typeof window === 'undefined') return 'none';

  // Check for Facebook Instant Games
  if (window.FBInstant) {
    return 'facebook';
  }

  // Check for YouTube Games
  if (window.ytgame) {
    return 'youtube';
  }

  // Check for Reddit (when their SDK becomes available)
  // For now, we'll check URL or user agent
  if (window.location.href.includes('reddit.com')) {
    return 'reddit';
  }

  return 'none';
}

// Facebook Instant Games SDK
class FacebookSDK implements InstantGamesSDK {


  async initialise(): Promise<void> {
    if (!window.FBInstant) {
      throw new Error('Facebook Instant Games SDK not available');
    }

    try {
      await window.FBInstant.initializeAsync();

    } catch (error) {
      console.error('Failed to initialize Facebook SDK:', error);
      throw error;
    }
  }

  async startGame(): Promise<void> {
    if (!window.FBInstant) return;

    try {
      await window.FBInstant.startGameAsync();
    } catch (error) {
      console.error('Failed to start game:', error);
    }
  }

  async submitScore(score: number, leaderboard?: string): Promise<void> {
    if (!window.FBInstant) return;

    try {
      await window.FBInstant.setScoreAsync(score, leaderboard);
    } catch (error) {
      console.error('Failed to submit score:', error);
    }
  }

  async share(options: ShareOptions): Promise<void> {
    if (!window.FBInstant) return;

    try {
      await window.FBInstant.shareAsync({
        intent: 'SHARE',
        image: options.imageUrl ?? '',
        text: options.text ?? '',
        data: options.data ?? {},
      });
    } catch (error) {
      console.error('Failed to share:', error);
    }
  }

  onPause(callback: () => void): void {
    if (!window.FBInstant) return;

    window.FBInstant.onPause(callback);
  }

  onError(callback: (error: Error) => void): void {
    if (!window.FBInstant) return;

    window.FBInstant.onError(callback);
  }
}

// YouTube Games SDK
class YouTubeSDK implements InstantGamesSDK {


  async initialise(): Promise<void> {
    if (!window.ytgame) {
      throw new Error('YouTube Games SDK not available');
    }

    return new Promise((resolve, _reject) => {
      window.ytgame?.system.onReady(() => {
  
        resolve();
      });
    });
  }

  async startGame(): Promise<void> {
    // YouTube doesn't have an explicit start game call
    // Initialised
  }

  async submitScore(score: number): Promise<void> {
    // YouTube leaderboard submission - implementation depends on their API
    console.log('Submitting score to YouTube:', score);
  }

  async share(options: ShareOptions): Promise<void> {
    if (!window.ytgame?.share) {
      console.warn('YouTube share not available');
      return;
    }

    try {
      await window.ytgame.share({
        text: options.text,
      });
    } catch (error) {
      console.error('Failed to share on YouTube:', error);
    }
  }

  onPause(): void {
    // YouTube doesn't expose pause events
  }

  onError(): void {
    // YouTube doesn't expose error events in the same way
  }
}

class RedditSDK implements InstantGamesSDK {
  private requestCounter = 0;
  private pendingRequests = new Map<string, {
    resolve: (value: void) => void;
    reject: (error: Error) => void;
  }>();

  constructor() {
    if (window.devvitWebView) {
      window.devvitWebView.addEventListener('message', this.handleMessage.bind(this));
    }
  }

  private handleMessage(event: MessageEvent): void {
    const response = event.data as { requestId: string; success: boolean; error?: string };
    const pending = this.pendingRequests.get(response.requestId);
    if (!pending) {
      return;
    }

    this.pendingRequests.delete(response.requestId);

    if (response.success) {
      pending.resolve(undefined);
    } else {
      pending.reject(new Error(response.error ?? 'Unknown error'));
    }
  }

  private sendMessage(message: { type: string; requestId: string; [key: string]: unknown }): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!window.devvitWebView) {
        reject(new Error('Devvit WebView not available'));
        return;
      }

      this.pendingRequests.set(message.requestId, { resolve, reject });
      window.devvitWebView.postMessage(message);

      setTimeout(() => {
        if (this.pendingRequests.has(message.requestId)) {
          this.pendingRequests.delete(message.requestId);
          reject(new Error('Request timeout'));
        }
      }, 5000);
    });
  }

  async initialise(): Promise<void> {
    if (!window.devvitWebView) {
      console.warn('Devvit WebView not available, running in standalone mode');
      return;
    }

    const requestId = `init_${Date.now()}_${++this.requestCounter}`;
    await this.sendMessage({
      type: 'init',
      requestId,
    });
  }

  async startGame(): Promise<void> {
    // Devvit does not require explicit startGame call
  }

  async submitScore(score: number, leaderboard?: string): Promise<void> {
    if (!window.devvitWebView) {
      console.warn('Devvit WebView not available, cannot submit score');
      return;
    }

    try {
      const requestId = `score_${Date.now()}_${++this.requestCounter}`;
      await this.sendMessage({
        type: 'submit_score',
        score,
        leaderboard: leaderboard ?? 'global',
        requestId,
      });
    } catch (error) {
      console.error('Failed to submit score to Reddit:', error);
    }
  }

  async share(options: ShareOptions): Promise<void> {
    if (!window.devvitWebView) {
      console.warn('Devvit WebView not available, cannot share');
      return;
    }

    try {
      const requestId = `share_${Date.now()}_${++this.requestCounter}`;
      await this.sendMessage({
        type: 'share',
        text: options.text,
        requestId,
      });
    } catch (error) {
      console.error('Failed to share on Reddit:', error);
    }
  }

  onPause(): void {
    // Devvit handles pause/resume through page visibility
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Game paused
      }
    });
  }

  onError(callback: (error: Error) => void): void {
    window.addEventListener('error', (event) => {
      callback(new Error(event.message));
    });
  }
}

// No platform (standard web)
class WebSDK implements InstantGamesSDK {


  async initialise(): Promise<void> {
    // Initialised
  }

  async startGame(): Promise<void> {
    // Initialised
  }

  async submitScore(): Promise<void> {
    // Standard web doesn't have platform leaderboards
  }

  async share(options: ShareOptions): Promise<void> {
    if (navigator.share) {
      try {
        await navigator.share({
          title: options.text ?? PRODUCT_FULL_NAME,
          text: options.text,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Share cancelled or failed:', error);
      }
    }
  }

  onPause(): void {
    // Handle visibility change for web
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // App is hidden
      }
    });
  }

  onError(): void {
    window.addEventListener('error', (event) => {
      console.error('Global error:', event.error);
    });
  }
}

// SDK factory
export function createSDK(platform: InstantGamesPlatform): InstantGamesSDK {
  switch (platform) {
    case 'facebook':
      return new FacebookSDK();
    case 'youtube':
      return new YouTubeSDK();
    case 'reddit':
      return new RedditSDK();
    default:
      return new WebSDK();
  }
}

// Hook for using the instant games SDK
export interface UseInstantGamesReturn {
  platform: InstantGamesPlatform;
  sdk: InstantGamesSDK;
  isReady: boolean;
  initialise: () => Promise<void>;
  startGame: () => Promise<void>;
  submitScore: (score: number, leaderboard?: string) => Promise<void>;
  share: (options: ShareOptions) => Promise<void>;
}

export function createInstantGamesHook(): UseInstantGamesReturn {
  const platform = detectPlatform();
  const sdk = createSDK(platform);
  let isReady = false;

  return {
    platform,
    sdk,
    get isReady() {
      return isReady;
    },
    async initialise() {
      await sdk.initialise();
      isReady = true;
    },
    async startGame() {
      await sdk.startGame();
    },
    async submitScore(score: number, leaderboard?: string) {
      await sdk.submitScore(score, leaderboard);
    },
    async share(options: ShareOptions) {
      await sdk.share(options);
    },
  };
}

// Singleton instance
let instantGamesInstance: UseInstantGamesReturn | null = null;

export function getInstantGames(): UseInstantGamesReturn {
  if (!instantGamesInstance) {
    instantGamesInstance = createInstantGamesHook();
  }
  return instantGamesInstance;
}
